import { useEffect, useRef, useState } from 'react';
import { App, Button, Checkbox, Form, Input } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useLinkWithPasskey, useLoginWithOAuth, useLoginWithPasskey, usePrivy } from '@privy-io/react-auth';
import type { User } from '@privy-io/react-auth';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, useAuth } from '../../auth/AuthContext';
import { getRememberedCredentials, getSession, hasPasskeyLoginSuccess, isSessionValid, markPasskeyLoginSuccess, markPasskeySetup } from '../../auth/storage';
import Logo from '../../components/Logo/Logo';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import './Auth.css';

type LoginForm = {
    email: string;
    password: string;
    remember: boolean;
};

type AuthApiPayload = {
    success?: boolean;
    message?: string;
    accessToken?: string;
};

function isSuccessResponse(response: { status: number; data?: AuthApiPayload }) {
    const httpOk = response.status === 200 || response.status === 201;
    const data = response.data;
    if (data?.accessToken) return true;
    return httpOk && data?.success !== false;
}

function GoogleMark() {
    return (
        <svg className="auth__brand-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.21 3.31v2.77h3.57C21.36 18.42 22.56 15.6 22.56 12.25z"
            />
            <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
            />
            <path
                fill="#FBBC05"
                d="M5.84 14.09A6.6 6.6 0 0 1 5.49 12c0-.72.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
        </svg>
    );
}

function AppleMark() {
    return (
        <svg className="auth__brand-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path
                fill="currentColor"
                d="M16.37 1.43c.04.15.05.3.05.43 0 1.14-.49 2.27-1.18 3.08-.74.9-1.99 1.57-2.98 1.57-.12 0-.24-.02-.31-.03-.01-.06-.04-.22-.04-.39 0-1.15.57-2.27 1.2-2.98.81-.94 2.15-1.64 3.26-1.68zM20.93 17.14c-.03.07-.46 1.58-1.52 3.12-.94 1.37-1.94 2.71-3.43 2.71-1.52 0-1.9-.88-3.63-.88-1.7 0-2.3.91-3.67.91-1.38 0-2.33-1.26-3.43-2.8-1.29-1.82-2.32-4.63-2.32-7.28 0-4.28 2.8-6.55 5.55-6.55 1.45 0 2.68.95 3.6.95.88 0 2.32-1.01 4-1.01.63 0 2.89.06 4.38 2.19-.13.09-2.56 1.49-2.56 4.43 0 3.53 3.1 4.76 3.21 4.8z"
            />
        </svg>
    );
}

type PasskeyGate = {
    settled: boolean;
    seenChallenge: boolean;
    resolve: () => void;
    reject: (error: unknown) => void;
};

function passkeyIdsOf(user: User | null | undefined) {
    return (
        user?.linkedAccounts.flatMap((account) =>
            account.type === 'passkey' ? [account.credentialId] : []
        ) ?? []
    );
}

function passkeyAccountKeys(user: User | null | undefined) {
    const keys: string[] = [];
    if (user?.id) keys.push(user.id);
    const email = user?.email?.address || getSession()?.email;
    if (typeof email === 'string' && email.trim()) keys.push(email.trim().toLowerCase());
    return keys;
}

function delay(ms: number) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function Login() {
    const { t } = useTranslation('auth');
    const { login, loginWithGoogle, logout, startPasskeyGate, finishPasskeyGate, passkeyGateOpen } = useAuth();
    const { ready, authenticated, user: privyUser } = usePrivy();
    const { loginWithPasskey, state: passkeyState } = useLoginWithPasskey();
    const { message, notification } = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm<LoginForm>();
    const [submitting, setSubmitting] = useState(false);
    const [googleBackendSyncing, setGoogleBackendSyncing] = useState(false);
    const remembered = getRememberedCredentials();
    const from = (location.state as { from?: string } | null)?.from || '/home';
    const privySnapshot = useRef({ ready, authenticated, user: privyUser });
    const passkeyGate = useRef<PasskeyGate | null>(null);
    const linkingPasskey = useRef(false);
    const resumeGate = useRef(passkeyGateOpen);
    const loginWithPasskeyRef = useRef(loginWithPasskey);
    const { linkWithPasskey } = useLinkWithPasskey();
    const linkWithPasskeyRef = useRef(linkWithPasskey);
    privySnapshot.current = { ready, authenticated, user: privyUser };
    loginWithPasskeyRef.current = loginWithPasskey;
    linkWithPasskeyRef.current = linkWithPasskey;

    useEffect(() => {
        const gate = passkeyGate.current;
        if (linkingPasskey.current || !gate || gate.settled) return;

        if (passkeyState.status === 'generating-challenge' || passkeyState.status === 'awaiting-passkey') {
            gate.seenChallenge = true;
            return;
        }

        if (!gate.seenChallenge) return;

        if (passkeyState.status === 'submitting-response' || passkeyState.status === 'done') {
            gate.settled = true;
            passkeyGate.current = null;
            markPasskeySetup();
            gate.resolve();
            return;
        }

        if (passkeyState.status === 'error') {
            gate.settled = true;
            passkeyGate.current = null;
            gate.reject(passkeyState.error ?? new Error('passkey'));
        }
    }, [passkeyState]);

    const { initOAuth, loading: googleLoading } = useLoginWithOAuth({
        onComplete: async ({ loginMethod }) => {
            if (loginMethod !== 'google') return;

            setGoogleBackendSyncing(true);
            startPasskeyGate();
            try {
                await loginWithGoogle();
                await completeSignIn(t('login.googleSuccess'));
            } catch (error) {
                console.log('error', error);
                finishPasskeyGate();
                message.error(getApiErrorMessage(error, t('errors.google')));
            } finally {
                setGoogleBackendSyncing(false);
            }
        },
        onError: (error) => {
            console.log('error', error);
            message.error(getApiErrorMessage(error, t('errors.google')));
        },
    });

    const waitForPrivyUser = async () => {
        const started = Date.now();
        let stableId = '';
        let stableSince = 0;

        while (Date.now() - started < 12000) {
            const current = privySnapshot.current;
            const id = current.user?.id ?? '';

            if (current.ready && current.authenticated && current.user) {
                if (id !== stableId) {
                    stableId = id;
                    stableSince = Date.now();
                } else if (Date.now() - stableSince >= 500) {
                    return current.user;
                }
            }

            await delay(200);
        }

        return privySnapshot.current.user;
    };

    const verifyExistingPasskey = (credentialIds: string[]) =>
        new Promise<void>((resolve, reject) => {
            const gate: PasskeyGate = { settled: false, seenChallenge: false, resolve, reject };
            passkeyGate.current = gate;
            void loginWithPasskeyRef.current({ credentialIds }).catch((error) => {
                if (gate.settled) return;
                gate.settled = true;
                if (passkeyGate.current === gate) passkeyGate.current = null;
                reject(error);
            });
        });

    const setupPasskey = async () => {
        linkingPasskey.current = true;
        try {
            await linkWithPasskeyRef.current({ name: 'APC Plus passkey' });
        } finally {
            linkingPasskey.current = false;
        }
        markPasskeySetup();
    };

    const completeSignIn = async (successMessage: string) => {
        let settingUp = false;

        try {
            const currentUser = await waitForPrivyUser();
            const accountKeys = passkeyAccountKeys(currentUser);
            const credentialIds = passkeyIdsOf(currentUser);
            const knownDevice = hasPasskeyLoginSuccess(accountKeys);

            if (knownDevice) {
                if (!credentialIds.length) {
                    throw new Error(t('errors.passkey'));
                }
                message.info(t('login.verifyingPasskey'));
                await verifyExistingPasskey(credentialIds);
            } else {
                if (!privySnapshot.current.authenticated) {
                    throw new Error(t('errors.passkeySession'));
                }
                settingUp = true;
                message.info(
                    credentialIds.length ? t('login.setupPasskeyOnDevice') : t('login.setupPasskey')
                );
                await setupPasskey();
            }
            markPasskeyLoginSuccess(accountKeys);
            finishPasskeyGate();
            message.success(successMessage);
            navigate(from, { replace: true });
        } catch (error) {
            console.log('error', error);
            logout();
            message.error(
                getApiErrorMessage(error, settingUp ? t('errors.passkeySetup') : t('errors.passkey'))
            );
        }
    };

    useEffect(() => {
        if (!resumeGate.current) return;
        resumeGate.current = false;

        if (!isSessionValid()) {
            finishPasskeyGate();
            return;
        }

        void completeSignIn(t('login.success'));
    }, []);

    const onFinish = async ({ email, password, remember }: LoginForm) => {
        setSubmitting(true);
        startPasskeyGate();

        try {
            const response = await login(email, password, Boolean(remember));
            if (!isSuccessResponse(response)) {
                logout();
                notification.error({
                    message: t('errors.signIn'),
                    description: getApiErrorMessage(response.data, t('errors.signIn')),
                });
                return;
            }
            await completeSignIn(t('login.success'));
        } catch (error) {
            console.log('error', error);
            finishPasskeyGate();
            message.error(getApiErrorMessage(error, t('errors.signIn')));
        } finally {
            setSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            await initOAuth({ provider: 'google' });
        } catch (error) {
            console.log('error', error);
            message.error(getApiErrorMessage(error, t('errors.google')));
        }
    };

    return (
        <div className="auth">
            <div className="auth__card">
                <div className="auth__language">
                    <LanguageSwitcher compact />
                </div>
                <div className="auth__brand">
                    <Logo />
                </div>

                <h1 className="auth__title">{t('login.title')}</h1>
                <p className="auth__subtitle">{t('login.subtitle')}</p>

                <div className="auth__social">
                    <Button
                        htmlType="button"
                        className="auth__social-btn auth__social-btn--google"
                        icon={<GoogleMark />}
                        loading={googleLoading || googleBackendSyncing}
                        disabled={googleBackendSyncing}
                        onClick={handleGoogleLogin}
                    >
                        {t('login.google')}
                    </Button>
                    {/* <Button
                        htmlType="button"
                        className="auth__social-btn auth__social-btn--apple"
                        icon={<AppleMark />}
                    >
                        Sign in with Apple
                    </Button> */}
                </div>

                <div className="auth__divider">
                    <span>{t('login.or')}</span>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        email: remembered.email,
                        password: remembered.password,
                        remember: Boolean(remembered.email),
                    }}
                >
                    <Form.Item
                        name="email"
                        label={t('fields.email')}
                        rules={[
                            { required: true, message: t('validation.emailRequired') },
                            { type: 'email', message: t('validation.emailInvalid') },
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder={t('fields.email')}
                            autoComplete="email"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={t('fields.password')}
                        rules={[{ required: true, message: t('validation.passwordRequired') }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={t('fields.password')}
                            autoComplete="current-password"
                        />
                    </Form.Item>

                    <div className="auth__extra">
                        <Form.Item name="remember" valuePropName="checked" noStyle>
                            <Checkbox>{t('login.remember')}</Checkbox>
                        </Form.Item>
                        <Link to="/reset-password" className="auth__forgot">
                            {t('login.forgot')}
                        </Link>
                    </div>

                    <Button type="primary" htmlType="submit" loading={submitting}>
                        {t('login.submit')}
                    </Button>
                </Form>

                <p className="auth__footer">
                    {t('login.noAccount')} <Link to="/register">{t('login.createOne')}</Link>
                </p>
            </div>
        </div>
    );
}

export default Login;
 